import React, { useEffect, useState } from 'react';
import { AlignLeft, ListChecks, Pencil, Trash2 } from 'lucide-react';
import { cn } from '../../utils/cn';
import { SHAPE_FILLS, SHAPE_STROKES, hueVars, isNoStroke, isPlainFill } from './palette';

const toolButton = 'inline-flex h-11 min-w-11 items-center justify-center gap-1.5 rounded-xl px-3 text-sm font-medium transition-colors';

const FillDot = ({ color, className }) => (
  <span
    className={cn('mm-swatch-fill', isPlainFill(color) && 'mm-fill-plain', className)}
    style={hueVars(color, color)}
  />
);

const StrokeDot = ({ color, className }) => (
  <span
    className={cn('mm-swatch-stroke', isNoStroke(color) && 'mm-stroke-none', className)}
    style={hueVars(color, color)}
  />
);

const Picker = ({ title, options, value, onPick, Dot }) => (
  <div className="pointer-events-auto mb-2 rounded-2xl border border-border bg-background/95 p-3 shadow-lg backdrop-blur">
    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
    <div className="grid grid-cols-9 gap-1.5" role="radiogroup" aria-label={title}>
      {options.map(({ key, label }) => (
        <button
          key={key}
          type="button"
          role="radio"
          aria-checked={value === key}
          aria-label={label}
          title={label}
          onClick={() => onPick(key)}
          className={cn(
            'grid h-7 w-7 place-items-center rounded-full ring-offset-2 ring-offset-background transition-transform hover:scale-110',
            value === key && 'ring-2 ring-foreground/70'
          )}
        >
          <Dot color={key} className="h-5 w-5" />
        </button>
      ))}
    </div>
  </div>
);

const ShapeToolbar = ({ shape, onEdit, onKind, onFill, onStroke, onDelete }) => {
  const [picker, setPicker] = useState(null);

  useEffect(() => setPicker(null), [shape.id]);

  const togglePicker = (name) => setPicker((current) => (current === name ? null : name));

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex flex-col items-center px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      {picker === 'fill' && (
        <Picker title="Fill" options={SHAPE_FILLS} value={shape.fill} onPick={onFill} Dot={FillDot} />
      )}
      {picker === 'stroke' && (
        <Picker title="Outline" options={SHAPE_STROKES} value={shape.stroke} onPick={onStroke} Dot={StrokeDot} />
      )}
      <div
        role="toolbar"
        aria-label="Note actions"
        className="pointer-events-auto flex items-center gap-1 rounded-2xl border border-border bg-background/95 p-1.5 shadow-lg backdrop-blur"
      >
        <button type="button" className={cn(toolButton, 'text-foreground hover:bg-accent')} aria-label="Edit" title="Edit" onClick={onEdit}>
          <Pencil className="h-5 w-5" />
        </button>

        <div className="flex rounded-xl bg-muted p-0.5" role="radiogroup" aria-label="Note type">
          {[
            { kind: 'TEXT', label: 'Text', Icon: AlignLeft },
            { kind: 'CHECKLIST', label: 'Checklist', Icon: ListChecks },
          ].map(({ kind, label, Icon }) => (
            <button
              key={kind}
              type="button"
              role="radio"
              aria-checked={shape.kind === kind}
              aria-label={label}
              title={label}
              onClick={() => onKind(kind)}
              className={cn(
                'inline-flex h-10 min-w-10 items-center justify-center gap-1.5 rounded-[10px] px-2.5 text-sm font-medium transition-colors',
                shape.kind === kind ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Icon className="h-[18px] w-[18px]" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        <button
          type="button"
          className={cn(toolButton, 'hover:bg-accent', picker === 'fill' && 'bg-accent')}
          aria-label="Fill color"
          aria-expanded={picker === 'fill'}
          title="Fill color"
          onClick={() => togglePicker('fill')}
        >
          <FillDot color={shape.fill} className="h-5 w-5" />
        </button>
        <button
          type="button"
          className={cn(toolButton, 'hover:bg-accent', picker === 'stroke' && 'bg-accent')}
          aria-label="Outline color"
          aria-expanded={picker === 'stroke'}
          title="Outline color"
          onClick={() => togglePicker('stroke')}
        >
          <StrokeDot color={shape.stroke} className="h-5 w-5" />
        </button>

        <button
          type="button"
          className={cn(toolButton, 'text-destructive hover:bg-destructive/10')}
          aria-label="Delete note"
          title="Delete note"
          onClick={onDelete}
        >
          <Trash2 className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default ShapeToolbar;
